import { VehicleEntry } from ".";
import { dateFormatter, dateWindowFormatter } from "utils/utils";
import { Grid, GridColumn as Column, GridNoRecords, GridCustomRowProps } from '@progress/kendo-react-grid';
import { useEffect, useMemo, useState } from "react";
import { fetchApi } from "services/api";
import { xIcon } from "@progress/kendo-svg-icons";
import VehicleNumberCell from "./VehicleNumberCell";
import { Checkbox } from "@progress/kendo-react-inputs";
import { usePrevious } from "utils/usePrevious";
import { SvgIcon } from "@progress/kendo-react-common";

type QuoteFinderVehicleProps = {
  vehicles: VehicleEntry[];
  addVehicle: (vehicleID: number) => void;
  removeVehicle: (vehicleID: number) => void;
  toggleSelected: (vehicleID: number) => void;
  toggleSelectedAll: (select: boolean) => void;
}

export type Vehicle = {
  VehicleID: number;
  VehicleNumber: string;
  Status: string;
  StatusColor: string;
  AvailableLocation: string;
  AvailableDateTime: string;
  AvailabilityNote: string;
  Schedule: VehicleScheduleChunk[];
}

export type VehicleScheduleChunk = {
  Type: ScheduleChunkType;
  Start: string | null;
  End: string | null;
}

export enum ScheduleChunkType {
  OOS = 0,
  Available = 1,
  Repositioning = 2,
  Planned = 3,
  Loaded = 4,
}

const formatScheduleText = (note: string, schedule: VehicleScheduleChunk[]) => {
  const filteredChunks = schedule.filter(chunk => chunk.Type !== ScheduleChunkType.Available);
  let scheduleText = '';

  if (filteredChunks.length === 0) {
    scheduleText = `Available from ${dateFormatter(schedule[0].Start)}`;
  } else {
    scheduleText = filteredChunks
      .map(chunk => `${ScheduleChunkType[chunk.Type]} ${(chunk.Start ? "from" : "until")} ${dateWindowFormatter(chunk.Start || chunk.End, chunk.End)}`)
      .join(". ");
  }

  return note ? `${scheduleText}. ${note}` : scheduleText;
};

export const Vehicles = (props: QuoteFinderVehicleProps) => {
  const [vehicleData, setVehicleData] = useState<Vehicle[]>([]);
  const vehicleIds = useMemo(() => props.vehicles.map(v => v.VehicleID), [props.vehicles]);
  const [vehicleDataLoading, setVehicleDataLoading] = useState(false);
  const prevVehicleIds = usePrevious(vehicleIds);

  useEffect(() => {
    const fetchVehicles = () => {
      setVehicleDataLoading(true);
      fetchApi("/api/Quote/FinderVehiclesSchedule", { VehicleIDs: vehicleIds }, "POST")
        .then(data => setVehicleData(data.Vehicles))
        .finally(() => setVehicleDataLoading(false));
    }

    if (vehicleIds.length === 0) {
      setVehicleData([]);
      setVehicleDataLoading(false);
    } else if (JSON.stringify(vehicleIds) !== JSON.stringify(prevVehicleIds)) {
      fetchVehicles();
    }
  }, [vehicleIds, prevVehicleIds]);

  const VehicleRow = (rowProps: GridCustomRowProps) => {
    return <tr {...rowProps.trProps} className={props.vehicles.some(v => v.VehicleID === rowProps.dataItem.VehicleID && v.Selected) ? "" : "text-muted"}>
      {rowProps.children}
    </tr>
  }

  return <div className="mt-2">
    <Grid data={vehicleData} rows={{ data: VehicleRow }} size="small">
      <GridNoRecords>
        {vehicleDataLoading ? "Loading..." : "Please add vehicles to view schedules."}
      </GridNoRecords>
      <Column
        title=""
        width={32}
        cells={{
          data: (cell) => {
            const vehicle = cell.dataItem as Vehicle;
            return <td {...cell.tdProps}>
              <Checkbox
                checked={props.vehicles.some(v => v.VehicleID === vehicle.VehicleID && v.Selected)}
                onChange={() => props.toggleSelected(vehicle.VehicleID)}
              />
            </td>;
          },
          headerCell: () => (
            <th>
              <Checkbox
                checked={props.vehicles.some(v => v.Selected)}
                onChange={(e) => props.toggleSelectedAll(e.value)}
              />
            </th>
          )
        }}
      />
      <Column field="VehicleNumber" title="Vehicle #" width={100} cells={{ data: VehicleNumberCell }} />
      <Column field="Status" title="Status" width={100} cells={{ data: (props) => <td {...props.tdProps} style={{ color: props.dataItem.StatusColor }}>{props.dataItem.Status}</td> }} />
      <Column field="AvailableLocation" title="Next Available" width={150} />
      <Column field="Schedule" title="Schedule" cells={{ data: (props) => <td {...props.tdProps}>{formatScheduleText(props.dataItem.AvailabilityNote, props.dataItem.Schedule)}</td> }} />
      <Column title="" width={26} cells={{ data: (cell) => {
        const vehicle = cell.dataItem as Vehicle;
        return <td {...cell.tdProps}>
          <a
            href="#"
            className="text-danger"
            onClick={(e) => {
              e.preventDefault();
              props.removeVehicle(vehicle.VehicleID);
            }}
          >
            <SvgIcon icon={xIcon} />
          </a>
        </td>;
      } }} />
    </Grid>
  </div>
}
